'use client';

import { StreamChat } from 'stream-chat';

import { createContext, useContext, useEffect, useState } from 'react';

import { useProfile } from '@/hooks/useProfile';
import { chatService } from '@/lib/services/chat/ChatService';

import { useAuth } from './AuthContext';

// Module-level: track pending disconnect so new mounts wait for it to finish
let disconnectPromise: Promise<void> | null = null;

type StreamChatContextType = {
  client: StreamChat | null;
  isReady: boolean;
};

const StreamChatContext = createContext<StreamChatContextType>({
  client: null,
  isReady: false,
});

export function StreamChatProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  const { profile } = useProfile();
  const [client, setClient] = useState<StreamChat | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!user || !profile) return;

    let isSubscribed = true;
    const chatClient = StreamChat.getInstance(
      process.env.NEXT_PUBLIC_STREAM_API_KEY!
    );

    const setupClient = async () => {
      try {
        // Wait for any in-flight disconnect to finish before connecting
        if (disconnectPromise) {
          await disconnectPromise;
          disconnectPromise = null;
        }

        if (!isSubscribed) return;

        if (chatClient.userID) {
          // Already connected (e.g., Strict Mode remount where cleanup hasn't run yet)
          if (isSubscribed) {
            setClient(chatClient);
            setIsReady(true);
          }
          return;
        }

        const { token, stream_chat_id } =
          await chatService.initializeStreamUser();

        if (!isSubscribed) return;

        await chatClient.connectUser(
          {
            id: stream_chat_id,
            name: profile.display_name || '',
            image: profile.image_url,
          },
          token
        );

        if (isSubscribed) {
          setClient(chatClient);
          setIsReady(true);
        }
      } catch (error) {
        console.error('Error connecting Stream Chat:', error);
      }
    };

    setupClient();

    return () => {
      isSubscribed = false;
      setClient(null);
      setIsReady(false);
      disconnectPromise = chatClient
        .disconnectUser()
        .catch(console.error) as Promise<void>;
    };
  }, [user?.id, profile?.display_name, profile?.image_url]);

  return (
    <StreamChatContext.Provider value={{ client, isReady }}>
      {children}
    </StreamChatContext.Provider>
  );
}

export function useStreamChat() {
  const context = useContext(StreamChatContext);
  if (!context) {
    throw new Error('useStreamChat must be used within StreamChatProvider');
  }
  return context;
}
